/**
 * product-misc.js
 *
 * Local reimplementation of the "product-misc" cluster of custom elements that
 * used to be supplied by the remote the vendor bundle
 * (the vendor runtime, formerly layout/theme.liquid:28-36):
 *
 *   <dynamic-dates>        — estimated-delivery / shipping-timeline date tokens
 *   <custom-product-field> — extra line-item-property field on the product page
 *
 * Neither has an upstream in Shopify's Dawn theme, so both are derived purely
 * from this theme's own Liquid, CSS and schema. Every selector, class, id and
 * data attribute used below was read out of the theme; the file + line it came
 * from is quoted in a comment next to it.
 *
 * Loading: classic script, `defer`, alongside assets/secondary.js
 * (layout/theme.liquid:37). No imports, no modules, no external requests.
 *
 * The whole file is one IIFE so it declares NO globals and therefore cannot
 * collide with the top-level `const`/`class`/`function` bindings in
 * assets/secondary.js (`defaultDirection`, `isRtl`, `focusVisiblePolyfill`,
 * `hotspotButtons`, `registerHotspotButton`, `unregisterHotspotButton`) — classic
 * scripts share one global lexical scope. Helper names are prefixed `pmisc`
 * for the same reason. Nothing here defines `trapFocus`, `removeTrapFocus`,
 * `pauseAllMedia`, `debounce`, `fetchConfig`, `publish`/`subscribe` — those
 * belong to other clusters and this one needs none of them.
 *
 * The single deliberate exception is `window.themeCustomProductFields`, a small
 * read-only API published so the <product-form> cluster can consult this one
 * without either side having to guess the other's internals (see the bottom of
 * the file).
 */
(function () {
  'use strict';

  /* ================================================================== *
   * Shared local helpers                                                *
   * ================================================================== */

  /**
   * Wraps an event handler / callback so one failure can never bubble out and
   * break unrelated theme JS — in particular the add-to-cart flow, which is
   * bound in another file (layout/theme.liquid:702-709).
   */
  function pmiscGuard(fn, context) {
    return function () {
      try {
        return fn.apply(context || this, arguments);
      } catch (error) {
        if (window.console && console.warn) console.warn('[product-misc]', error);
      }
    };
  }

  /** Guarded customElements.define, per the convention in assets/theme-infra.js. */
  function pmiscDefine(tagName, constructor) {
    try {
      if (!customElements.get(tagName)) customElements.define(tagName, constructor);
    } catch (error) {
      if (window.console && console.warn) console.warn('[product-misc] define ' + tagName, error);
    }
  }

  /** Runs `fn` once the document has been parsed. */
  function pmiscWhenReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /**
   * Escapes a value for use inside a double-quoted CSS attribute selector.
   * `data-section` / `data-name` / `id` are merchant- or Shopify-supplied and
   * are never passed through `| escape` in the Liquid, so they can legitimately
   * contain characters that would break a selector.
   */
  function pmiscAttrEscape(value) {
    return String(value == null ? '' : value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  /** querySelectorAll that returns a real Array and never throws on a bad selector. */
  function pmiscQueryAll(selector, root) {
    try {
      return Array.prototype.slice.call((root || document).querySelectorAll(selector));
    } catch (error) {
      return [];
    }
  }

  /* ================================================================== *
   * <dynamic-dates>                                                     *
   * ================================================================== *
   *
   * Render sites — EXHAUSTIVE, verified with `grep -rn '<dynamic-dates'`:
   *   snippets/shipping-checkpoints.liquid:17 (opens) / :155 (closes)
   *     rendered from sections/main-product.liquid:837-838
   *   snippets/estimated-shipping.liquid:1 (opens) / :32 (closes)
   *     rendered from sections/main-product.liquid:835-836
   * Both sit inside <product-info id="ProductInfo-{{ section.id }}">
   * (sections/main-product.liquid:113-118).
   *
   * Element attributes:
   *   data-date-format   snippets/shipping-checkpoints.liquid:18,
   *                      snippets/estimated-shipping.liquid:3
   *   data-day-labels    shipping-checkpoints.liquid:19, estimated-shipping.liquid:4
   *   data-month-labels  shipping-checkpoints.liquid:20, estimated-shipping.liquid:5
   * `style` (--margin-top / --margin-bottom / --item-count) and `class` are
   * Liquid-owned and are never read or written here — `--item-count` /
   * `--item-count-sub` drive the CSS grid and the progress bar
   * (assets/base.css `.shipping-checkpoints`, `.shipping-checkpoints__bar`).
   *
   * Descendants rewritten — the ONLY node set this element touches:
   *   [data-dynamic-date="true"]  (written with single quotes in the Liquid:
   *     shipping-checkpoints.liquid:44, :53, :75, :84, :106, :115, :137, :146;
   *     estimated-shipping.liquid:25)
   *   with data-text / data-min-days / data-max-days on the same node
   *     (shipping-checkpoints.liquid:45-47 etc.; estimated-shipping.liquid:26-28)
   *
   * The element adds, removes and reorders NOTHING: base.css relies on the
   * structural selectors `.shipping-checkpoint p + p` and
   * `.estimated-shipping__icon:not(:empty)`.
   */

  /**
   * Schema defaults, used only as a fallback when a label list is missing or
   * short. Both attributes are always emitted by the Liquid, so this is purely
   * defensive.
   *   days   — sections/main-product.liquid:8520 (Monday-first)
   *   months — sections/main-product.liquid:8526 (January-first)
   */
  var PMISC_FALLBACK_DAYS = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ];
  var PMISC_FALLBACK_MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  /**
   * Splits a comma-separated label list and trims each entry — the schema
   * defaults put a space after every comma
   * (sections/main-product.liquid:8520, :8526, :8790, :8796).
   * Blank / missing entries fall back to `fallback` at the same index.
   */
  function pmiscSplitLabels(raw, fallback) {
    var parts = String(raw == null ? '' : raw).split(',');
    var out = [];
    for (var i = 0; i < fallback.length; i++) {
      var value = parts[i] == null ? '' : String(parts[i]).trim();
      out.push(value === '' ? fallback[i] : value);
    }
    return out;
  }

  /**
   * today + n calendar days.
   *
   * Uses setDate(getDate() + n) on a locally-constructed Date rather than
   * adding milliseconds, so a DST transition inside the window cannot shift the
   * result onto the wrong day.
   */
  function pmiscAddDays(days) {
    var date = new Date();
    date.setDate(date.getDate() + days);
    return date;
  }

  /** Zero-pads to two digits (for the `..._numeric` formats). */
  function pmiscPad2(value) {
    return value < 10 ? '0' + value : String(value);
  }

  /**
   * English ordinal suffix, required by the `day_mm_dd` / `mm_dd` schema option
   * labels "Monday, February 1st" / "February 1st"
   * (sections/main-product.liquid:8487, :8495).
   * There is no locale setting and no translation key for this anywhere in the
   * theme, so it is hard-coded English, including the standard 11th/12th/13th
   * exception.
   */
  function pmiscOrdinal(day) {
    var mod100 = day % 100;
    if (mod100 >= 11 && mod100 <= 13) return day + 'th';
    switch (day % 10) {
      case 1: return day + 'st';
      case 2: return day + 'nd';
      case 3: return day + 'rd';
      default: return day + 'th';
    }
  }

  /**
   * Formats `date` per `format`.
   *
   * The seven permitted values and the exact shape each must produce are fixed
   * by the option LABELS in the schema — sections/main-product.liquid:8484-8514
   * (estimated_shipping) and the identical list at :8754-8784
   * (shipping_checkpoints):
   *
   *   day_mm_dd          -> "Monday, February 1st"
   *   day_dd_mm          -> "Monday, 1. February"
   *   mm_dd              -> "February 1st"
   *   dd_mm              -> "1. February"
   *   dd_mm_no_dot       -> "1 February"
   *   day_dd_mm_numeric  -> "Monday, 01. 02."
   *   dd_mm_numeric      -> "01. 02."
   *
   * Day-of-week lookup is MONDAY-FIRST (the label defaults start at Monday:
   * sections/main-product.liquid:8520, :8790), hence (getDay() + 6) % 7.
   * An unrecognised or blank value degrades to `day_mm_dd`, the first schema
   * option and the estimated_shipping default (:8514), rather than throwing.
   */
  function pmiscFormatDate(date, format, days, months) {
    var dayLabel = days[(date.getDay() + 6) % 7];
    var monthLabel = months[date.getMonth()];
    var dayOfMonth = date.getDate();

    switch (format) {
      case 'day_dd_mm':
        return dayLabel + ', ' + dayOfMonth + '. ' + monthLabel;
      case 'mm_dd':
        return monthLabel + ' ' + pmiscOrdinal(dayOfMonth);
      case 'dd_mm':
        return dayOfMonth + '. ' + monthLabel;
      case 'dd_mm_no_dot':
        return dayOfMonth + ' ' + monthLabel;
      case 'day_dd_mm_numeric':
        return dayLabel + ', ' + pmiscPad2(dayOfMonth) + '. ' + pmiscPad2(date.getMonth() + 1) + '.';
      case 'dd_mm_numeric':
        return pmiscPad2(dayOfMonth) + '. ' + pmiscPad2(date.getMonth() + 1) + '.';
      case 'day_mm_dd':
      default:
        return dayLabel + ', ' + monthLabel + ' ' + pmiscOrdinal(dayOfMonth);
    }
  }

  /** Global (all-occurrences) literal token replacement, ES2019-safe. */
  function pmiscReplaceAll(subject, token, value) {
    return subject.split(token).join(value);
  }

  var PmiscDynamicDates = (function () {
    function PmiscDynamicDates() {
      return Reflect.construct(HTMLElement, [], PmiscDynamicDates);
    }

    PmiscDynamicDates.prototype = Object.create(HTMLElement.prototype, {
      constructor: { value: PmiscDynamicDates, writable: true, configurable: true }
    });
    Object.setPrototypeOf(PmiscDynamicDates, HTMLElement);

    /**
     * No user interaction, no network, no timer: the markup needs one pass and
     * the visitor's own clock. Re-running is safe (see `render`), so a
     * theme-editor re-render — which fires connectedCallback again on a fresh
     * element — needs no extra listener.
     */
    PmiscDynamicDates.prototype.connectedCallback = function () {
      try {
        this.render();
      } catch (error) {
        if (window.console && console.warn) console.warn('[product-misc] dynamic-dates', error);
      }
    };

    PmiscDynamicDates.prototype.render = function () {
      var format = this.dataset.dateFormat || '';
      var days = pmiscSplitLabels(this.dataset.dayLabels, PMISC_FALLBACK_DAYS);
      var months = pmiscSplitLabels(this.dataset.monthLabels, PMISC_FALLBACK_MONTHS);

      var nodes = pmiscQueryAll('[data-dynamic-date="true"]', this);

      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];

        /**
         * The template is ALWAYS read from `data-text`, never from the node's
         * current innerHTML. Liquid pre-renders a placeholder in which both
         * tokens are already replaced by a literal "-"
         * (snippets/shipping-checkpoints.liquid:49, :58, :80, :89, :111, :120,
         * :142, :151; snippets/estimated-shipping.liquid:30), so reading
         * innerHTML would permanently lose the tokens. Reading the attribute
         * instead makes the whole operation idempotent.
         */
        var template = node.getAttribute('data-text');
        if (template === null) continue; // attribute genuinely absent — leave the node alone

        var minDays = parseInt(node.dataset.minDays, 10) || 0;
        var maxDays = parseInt(node.dataset.maxDays, 10) || 0;

        var startDate = pmiscFormatDate(pmiscAddDays(minDays), format, days, months);
        var endDate = pmiscFormatDate(pmiscAddDays(maxDays), format, days, months);

        var html = pmiscReplaceAll(template, '[start_date]', startDate);
        html = pmiscReplaceAll(html, '[end_date]', endDate);

        /**
         * innerHTML, NOT textContent: `data-text` carries raw HTML. The backing
         * settings are `inline_richtext` (shipping checkpoints — default
         * "<strong>[start_date]</strong>", sections/main-product.liquid:8591-8599)
         * and `richtext` (estimated shipping — default
         * "<p>Get it between <strong>[start_date]</strong> and
         * <strong>[end_date]</strong>.</p>", sections/main-product.liquid:8462-8467).
         * assets/base.css styles `.estimated-shipping__text p` and
         * `.estimated-shipping__text p:last-child`, which only ever match if the
         * <p> children are really injected.
         *
         * An empty `data-text` is legitimate and writing '' is correct: the
         * wrapper `{% unless top == blank and bottom == blank %}`
         * (snippets/shipping-checkpoints.liquid:31, :62, :93, :124) skips a
         * checkpoint only when BOTH texts are blank, so a checkpoint with just a
         * top text still emits an empty `data-text` bottom paragraph.
         */
        node.innerHTML = html;
      }
    };

    return PmiscDynamicDates;
  })();

  pmiscDefine('dynamic-dates', PmiscDynamicDates);

  /* ================================================================== *
   * <custom-product-field>                                              *
   * ================================================================== *
   *
   * Render site — EXHAUSTIVE, verified with `grep -rn 'custom-product-field'`:
   *   snippets/custom-product-field.liquid:13 (opens) / :147 (closes)
   *     rendered from sections/main-product.liquid:839-840
   * Emitted only when `block.settings.field_name != blank`
   * (snippets/custom-product-field.liquid:12).
   *
   * THE CENTRAL STRUCTURAL FACT: the element is a SIBLING of the buy-buttons
   * block, i.e. it is rendered OUTSIDE
   * `<form id="product-form-{{ section.id }}" data-type="add-to-cart-form">`
   * (snippets/buy-buttons.liquid:80-86; the id is assigned at
   * sections/main-product.liquid:119). So the radios' own
   * `name='{{ block.settings.field_name }}'` (:78, :117) is NOT a form control —
   * nothing submits it, and the value has to be carried into the form by JS.
   *
   * Element attributes (snippets/custom-product-field.liquid:14-28):
   *   class="custom-product-field"      :14   (base.css `.custom-product-field{display:block}`)
   *   id='CustomField-<section>-<block>' :15  (unique; referenced nowhere else)
   *   data-type                          :16   text|number|textarea|pills|select
   *                                            — `| replace: 'swatches', 'pills'`
   *                                            means "swatches" NEVER reaches JS
   *   data-name                          :17   the line-item property key
   *   data-default-value                 :18   Liquid's idea of the initial value
   *   data-required                      :19   literal "true"/"false"
   *   data-section                       :20   equals sections/main-product.liquid `section.id`
   *   data-atc-error-msg                 :21
   *   data-not-main='true'               :23   (never emitted in this theme —
   *                                            no caller passes `not_main`)
   *   data-apply-sticky-atc-error        :25   (always emitted in this theme)
   *
   * Controls (all inside div.product-form__input.small-field__field, :30):
   *   text     input[type=text].input--small             :37-48
   *   number   input[type=number].input--small           :50-64
   *   textarea textarea.input--small.textarea--*         :66-73
   *   pills    input[type=radio][name=<data-name>]       :76-84   (index 0 `checked`)
   *   swatches same radios inside .color-swatches-container :94-131 (aliased to pills)
   *   select   select.select__select                     :134-141
   *   error    span.input-error-msg  (LAST child of .product-form__input) :145
   *
   * External DOM this component drives:
   *   button#ProductSubmitButton-<section>  snippets/buy-buttons.liquid:111-161
   *     — the ONLY element carrying data-required-fields="0" (:120) and
   *       data-valid-fields="0" (:121); data-unavailable="true" (:118) marks
   *       "disabled because sold out"; span.main-atc__error (:126) and
   *       span.main-atc__label.button__label (:127)
   *   button#SectionAtcBtn-<section>        buy-buttons.liquid:164-187 (secondary)
   *                                         AND sections/main-product.liquid:643-679
   *                                         (sticky) — the id is emitted TWICE for
   *                                         one section, so getElementById is unsafe
   *   span.sticky-atc__error / span.sticky-atc__label
   *                                         sections/main-product.liquid:653, :654
   *   <sticky-atc data-section="<section>"> sections/main-product.liquid:453-456
   *   other `.main-product-atc` buttons with no label/error spans:
   *     sections/rich-text.liquid:121, image-with-text.liquid:155,
   *     custom-columns.liquid:146, icons-with-content.liquid:189, results.liquid:155
   */

  /** Types whose control is a plain value input. */
  function pmiscIsFreeTextType(type) {
    return type === 'text' || type === 'number' || type === 'textarea';
  }

  /* ------------------------------------------------------------------ *
   * The per-section registry.                                           *
   *                                                                     *
   * Counts and gating are RECOMPUTED by rescanning the DOM rather than   *
   * maintained as +1/-1 deltas. That makes the whole thing idempotent    *
   * against a <product-info> subtree replacement (a variant change fires *
   * connectedCallback again on a fresh element) and against a            *
   * theme-editor block delete, neither of which can then leak a phantom  *
   * required field that would permanently disable add-to-cart.           *
   * ------------------------------------------------------------------ */
  var PmiscFieldRegistry = (function () {
    /** Section ids currently blocked because a required field is empty. */
    var gatedSections = Object.create(null);
    /** Every section id we have ever seen a field for, so we can re-sweep. */
    var knownSections = Object.create(null);
    /** rAF de-duplication for refresh(). */
    var pendingFrame = null;
    var pendingSections = Object.create(null);

    /**
     * Resolves the section a given add-to-cart control belongs to.
     *
     * Mirrors what layout/theme.liquid:702-709 already does for the
     * cross-section buttons: anything that only carries `.main-product-atc`
     * belongs to the main product form.
     */
    function resolveSectionId(element) {
      if (!element) return null;

      var id = element.id || '';
      var idMatch = id.match(/^(?:ProductSubmitButton|SectionAtcBtn)-(.+)$/);
      if (idMatch) return idMatch[1];

      // `featured-product-atc-{{ section.id }}` — snippets/buy-buttons.liquid:115, :167
      var className = typeof element.className === 'string' ? element.className : '';
      var classMatch = className.match(/featured-product-atc-(\S+)/);
      if (classMatch) return classMatch[1];

      // Nearest owning host that declares data-section.
      var host = null;
      try {
        host = element.closest('product-form[data-section], sticky-atc[data-section], product-info[data-section]');
      } catch (error) {
        host = null;
      }
      if (host) return host.getAttribute('data-section');

      // Plain `.main-product-atc` in another section — same fallback as theme.liquid:702.
      var mainForm = document.querySelector('product-form[data-main="true"]');
      if (mainForm) return mainForm.getAttribute('data-section');

      return null;
    }

    /** Every <custom-product-field> belonging to exactly this section. */
    function fieldsForSection(sectionId) {
      return pmiscQueryAll(
        'custom-product-field[data-section="' + pmiscAttrEscape(sectionId) + '"]'
      );
    }

    /**
     * The add-to-cart button set for a section.
     *
     * Class-based, not id-based, because `id="SectionAtcBtn-<section>"` is
     * emitted twice for one section when both the buy-buttons secondary button
     * (snippets/buy-buttons.liquid:165) and the sticky_atc block
     * (sections/main-product.liquid:644) are enabled.
     *
     * Sibling product-forms inside the same section — snippets/bundle-offer.liquid:640,
     * quantity-breaks.liquid:578, quantity-gifts.liquid:115, upsell-block.liquid:353 —
     * use `data-section="{{ section.id }}-{{ block.id }}"`, a different value, and
     * none of their buttons carry `main-product-atc`, so they are excluded.
     */
    function buttonsForSection(sectionId, notMain) {
      var escaped = pmiscAttrEscape(sectionId);
      var selector = notMain
        ? '.featured-product-atc-' + String(sectionId).replace(/["\\]/g, '')
        : '.main-product-atc';
      var buttons = pmiscQueryAll(selector);

      // Always include this section's own submit button, even in odd markup.
      var own = pmiscQueryAll('[id="ProductSubmitButton-' + escaped + '"]');
      for (var i = 0; i < own.length; i++) {
        if (buttons.indexOf(own[i]) === -1) buttons.push(own[i]);
      }
      return buttons;
    }

    /**
     * Disables a button on this component's behalf.
     *
     * `disabled` + `aria-disabled="true"` — assets/base.css styles
     * `.button.disabled`, `.button:disabled` and `.button[aria-disabled="true"]`
     * identically, so either renders correctly; both are set so the state is
     * also exposed to assistive tech.
     *
     * `data-pmisc-was-disabled` records whether the button was ALREADY disabled
     * before we touched it, so ungating can never re-enable a sold-out button
     * (snippets/buy-buttons.liquid:116-119, :169-171;
     * sections/main-product.liquid:649-651).
     */
    function gateButton(button) {
      if (button.getAttribute('data-pmisc-gated') !== 'true') {
        button.setAttribute('data-pmisc-was-disabled', button.disabled ? 'true' : 'false');
        button.setAttribute('data-pmisc-gated', 'true');
      }
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    }

    /** Reverses gateButton, but only for buttons we ourselves disabled. */
    function ungateButton(button) {
      if (button.getAttribute('data-pmisc-gated') !== 'true') return;

      var wasDisabled =
        button.getAttribute('data-pmisc-was-disabled') === 'true' ||
        // Belt and braces: #ProductSubmitButton flags sold-out explicitly
        // (snippets/buy-buttons.liquid:118). The attribute has no CSS anywhere
        // and exists purely so JS can tell "sold out" from "field empty".
        button.getAttribute('data-unavailable') === 'true';

      button.removeAttribute('data-pmisc-gated');
      button.removeAttribute('data-pmisc-was-disabled');

      if (!wasDisabled) {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }
    }

    /**
     * Swaps a button's label span for its error span.
     *
     * MUST use inline `style.display`, never a class. Both span pairs are
     * rendered with the error span already carrying inline `style="display:none;"`
     * (snippets/buy-buttons.liquid:126; sections/main-product.liquid:653) while
     * the label span carries no inline style at all (:127, :654) — and NEITHER
     * `.main-atc__error`/`.main-atc__label` nor `.sticky-atc__error`/`.sticky-atc__label`
     * has a single rule in ANY stylesheet (verified across assets/*.css).
     * base.css defines `.hidden{display:none!important}`, which could only ever
     * reinforce an inline `display:none`, never reverse it.
     *
     * Restoring uses '' so the span falls back to its UA default (`inline`),
     * which is what it had before we touched it.
     */
    function applyMessage(root, errorSelector, labelSelector, message, show) {
      var errorSpan = root.querySelector(errorSelector);
      var labelSpan = root.querySelector(labelSelector);

      if (show) {
        if (errorSpan) {
          errorSpan.textContent = message;
          errorSpan.style.display = '';
        }
        if (labelSpan) labelSpan.style.display = 'none';
      } else {
        if (errorSpan) {
          errorSpan.style.display = 'none';
          errorSpan.textContent = '';
        }
        if (labelSpan) labelSpan.style.display = '';
      }
    }

    /**
     * Recomputes counters, gating and messages for one section.
     */
    function refresh(sectionId) {
      if (!sectionId) return;
      knownSections[sectionId] = true;

      var fields = fieldsForSection(sectionId);

      var requiredCount = 0;
      var validCount = 0;
      var notMain = false;
      var applySticky = false;
      /**
       * LAST-WRITE-WINS: schema info at sections/main-product.liquid:10729 —
       * "If multiple required fields exist, the message from the last one will
       * be applied." Iterating in DOM order and overwriting gives exactly that.
       */
      var message = '';

      for (var i = 0; i < fields.length; i++) {
        var field = fields[i];
        if (field.dataset.notMain === 'true') notMain = true;
        if (field.dataset.applyStickyAtcError === 'true') applySticky = true;

        // data-required renders the literal string "true"/"false"
        // (snippets/custom-product-field.liquid:19, checkbox setting at :10717-10723).
        if (field.dataset.required !== 'true') continue;

        requiredCount++;
        if (typeof field.pmiscIsFilled === 'function' && field.pmiscIsFilled()) {
          validCount++;
        } else {
          message = field.dataset.atcErrorMsg || '';
        }
      }

      // The invariant the Liquid's two counters describe:
      // gated  <=>  Number(data-valid-fields) < Number(data-required-fields).
      var gated = validCount < requiredCount;

      if (gated) {
        gatedSections[sectionId] = true;
      } else {
        delete gatedSections[sectionId];
      }

      // Counters live only on #ProductSubmitButton-<section>
      // (snippets/buy-buttons.liquid:120-121) — the only element in the theme
      // that carries them.
      var submitButtons = pmiscQueryAll(
        '[id="ProductSubmitButton-' + pmiscAttrEscape(sectionId) + '"]'
      );
      for (var s = 0; s < submitButtons.length; s++) {
        submitButtons[s].setAttribute('data-required-fields', String(requiredCount));
        submitButtons[s].setAttribute('data-valid-fields', String(validCount));
      }

      var buttons = buttonsForSection(sectionId, notMain);
      for (var b = 0; b < buttons.length; b++) {
        var button = buttons[b];
        if (gated) gateButton(button);
        else ungateButton(button);

        // Only #ProductSubmitButton has these spans; the cross-section
        // `.main-product-atc` buttons have none, so applyMessage is a no-op there.
        applyMessage(button, '.main-atc__error', '.main-atc__label', message, gated);
      }

      /**
       * Sticky ATC mirror — only when data-apply-sticky-atc-error === "true"
       * (snippets/custom-product-field.liquid:25, setting `sticky_atc_error_msg`,
       * sections/main-product.liquid:10738-10744, default false). The attribute is
       * absent entirely on the `not_main` branch (:22-26), hence the notMain guard.
       */
      if (!notMain) {
        var stickies = pmiscQueryAll(
          'sticky-atc[data-section="' + pmiscAttrEscape(sectionId) + '"]'
        );
        for (var k = 0; k < stickies.length; k++) {
          applyMessage(
            stickies[k],
            '.sticky-atc__error',
            '.sticky-atc__label',
            message,
            gated && applySticky
          );
        }
      }
    }

    /** Coalesces refreshes for the same section into one animation frame. */
    function scheduleRefresh(sectionId) {
      if (!sectionId) return;
      pendingSections[sectionId] = true;
      if (pendingFrame !== null) return;

      pendingFrame = (window.requestAnimationFrame || window.setTimeout)(function () {
        pendingFrame = null;
        var ids = Object.keys(pendingSections);
        pendingSections = Object.create(null);
        for (var i = 0; i < ids.length; i++) {
          try {
            refresh(ids[i]);
          } catch (error) {
            if (window.console && console.warn) console.warn('[product-misc] refresh', error);
          }
        }
      }, 0);
    }

    /** Re-sweeps every section we have ever registered a field for. */
    function refreshAll() {
      var ids = Object.keys(knownSections);
      for (var i = 0; i < ids.length; i++) {
        try {
          refresh(ids[i]);
        } catch (error) {
          if (window.console && console.warn) console.warn('[product-misc] refreshAll', error);
        }
      }
    }

    function isGated(sectionId) {
      return !!(sectionId && gatedSections[sectionId]);
    }

    return {
      refresh: refresh,
      scheduleRefresh: scheduleRefresh,
      refreshAll: refreshAll,
      isGated: isGated,
      resolveSectionId: resolveSectionId,
      fieldsForSection: fieldsForSection
    };
  })();

  /* ------------------------------------------------------------------ *
   * Hard add-to-cart guard.                                             *
   *                                                                     *
   * Disabling the buttons is the visible half. This capture-phase pair   *
   * is the half that actually holds, because layout/theme.liquid:702-709 *
   * wires EVERY `[id^="SectionAtcBtn-"]` click straight to               *
   * `document.querySelector('product-form[data-main="true"]').handleSubmit(e)` — *
   * a gate applied only to the <form> submit event would be bypassed by   *
   * those buttons, and any other cluster that clears `disabled` in a      *
   * `finally` block (Dawn's ProductForm does exactly that) would silently *
   * unlock the gate.                                                     *
   *                                                                     *
   * Both listeners are installed once and do nothing at all unless the    *
   * resolved section is currently gated.                                 *
   * ------------------------------------------------------------------ */
  (function installAddToCartGuard() {
    /**
     * Controls that lead to an add-to-cart or an immediate checkout:
     *   .main-product-atc                 buy-buttons.liquid:115, :167;
     *                                     main-product.liquid:646; and the
     *                                     cross-section buttons listed above
     *   [class*="featured-product-atc-"]  buy-buttons.liquid:115, :167 (else branch)
     *   [id^="ProductSubmitButton-"]      buy-buttons.liquid:112
     *   [id^="SectionAtcBtn-"]            buy-buttons.liquid:165; main-product.liquid:644
     *   .shopify-payment-button           the dynamic-checkout wrapper emitted by
     *                                     `{{ form | payment_button }}`
     *                                     (buy-buttons.liquid:189-191)
     */
    var TRIGGER_SELECTOR = [
      '.main-product-atc',
      '[class*="featured-product-atc-"]',
      '[id^="ProductSubmitButton-"]',
      '[id^="SectionAtcBtn-"]',
      '.shopify-payment-button'
    ].join(', ');

    document.addEventListener(
      'click',
      pmiscGuard(function (event) {
        var target = event.target;
        if (!target || typeof target.closest !== 'function') return;

        var trigger = target.closest(TRIGGER_SELECTOR);
        if (!trigger) return;

        var sectionId = PmiscFieldRegistry.resolveSectionId(trigger);
        if (!PmiscFieldRegistry.isGated(sectionId)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
      }),
      true
    );

    document.addEventListener(
      'submit',
      pmiscGuard(function (event) {
        var form = event.target;
        if (!form || !form.id) return;

        // `product-form-{{ section.id }}` — sections/main-product.liquid:119,
        // consumed by snippets/buy-buttons.liquid:80-86.
        var match = String(form.id).match(/^product-form-(.+)$/);
        if (!match) return;
        if (!PmiscFieldRegistry.isGated(match[1])) return;

        event.preventDefault();
        event.stopImmediatePropagation();
      }),
      true
    );
  })();

  var PmiscCustomProductField = (function () {
    function PmiscCustomProductField() {
      return Reflect.construct(HTMLElement, [], PmiscCustomProductField);
    }

    PmiscCustomProductField.prototype = Object.create(HTMLElement.prototype, {
      constructor: { value: PmiscCustomProductField, writable: true, configurable: true }
    });
    Object.setPrototypeOf(PmiscCustomProductField, HTMLElement);

    var proto = PmiscCustomProductField.prototype;

    proto.connectedCallback = function () {
      try {
        this.pmiscSectionId = this.dataset.section || '';
        this.pmiscFieldName = this.dataset.name || '';
        this.pmiscType = this.dataset.type || 'text';

        /**
         * "Changed to empty" — schema info at sections/main-product.liquid:10736
         * for `field_error_message` says the field-level message is shown only
         * once a required value is CHANGED to empty, unlike the ATC-button
         * message (:10729) which applies whenever a required field is empty. So
         * the red border starts off and is armed by the first user interaction.
         */
        this.pmiscTouched = false;

        this.pmiscBindControls();
        this.pmiscSyncHiddenInput();

        // Refresh now, and again once the rest of the section has parsed — the
        // block loop (sections/main-product.liquid:121-122) can place this block
        // BEFORE the buy_buttons block, in which case #ProductSubmitButton does
        // not exist yet at upgrade time.
        PmiscFieldRegistry.refresh(this.pmiscSectionId);
        PmiscFieldRegistry.scheduleRefresh(this.pmiscSectionId);
      } catch (error) {
        if (window.console && console.warn) console.warn('[product-misc] custom-product-field', error);
      }
    };

    /**
     * Un-registers cleanly. Without this, a theme-editor block delete or a
     * <product-info> subtree replacement would leave a phantom required field
     * behind and permanently disable add-to-cart.
     *
     * The refresh is deferred because a re-render removes the old element and
     * inserts the new one in separate ticks; rescanning after both have settled
     * gives the correct count either way.
     */
    proto.disconnectedCallback = function () {
      try {
        this.pmiscUnbindControls();
        this.pmiscRemoveHiddenInput();
        PmiscFieldRegistry.scheduleRefresh(this.pmiscSectionId);
      } catch (error) {
        if (window.console && console.warn) console.warn('[product-misc] custom-product-field', error);
      }
    };

    /* ---------------------------------------------------------------- *
     * Value                                                             *
     * ---------------------------------------------------------------- */

    /**
     * Reads the current value, branching on `data-type`
     * (snippets/custom-product-field.liquid:16).
     *
     * Because :16 applies `| replace: 'swatches', 'pills'`, the string
     * "swatches" NEVER reaches JS — the swatch markup (:94-131) is handled by
     * the pills branch, which is correct: it uses the same
     * `input[type="radio"][name="<data-name>"]` group (:78 vs :117).
     */
    proto.pmiscGetValue = function () {
      try {
        if (this.pmiscType === 'pills') {
          var checked = this.querySelector('input[type="radio"]:checked');
          if (checked) return checked.value;

          // Two blocks may share one `field_name`, in which case the radios form
          // ONE document-wide group (name is the group key) and the checked
          // input may live in the sibling block. Fall back to the group.
          if (this.pmiscFieldName) {
            var shared = document.querySelector(
              'input[type="radio"][name="' + pmiscAttrEscape(this.pmiscFieldName) + '"]:checked'
            );
            if (shared) return shared.value;
          }
          return '';
        }

        if (this.pmiscType === 'select') {
          // snippets/custom-product-field.liquid:134-141. No <option> carries a
          // `selected` attribute (:139) and the `value=""` on the <select> (:136)
          // is invalid HTML that browsers ignore, so the first option always wins.
          var select = this.querySelector('select.select__select');
          return select ? select.value : '';
        }

        // text / number / textarea — snippets/custom-product-field.liquid:39, :52, :67
        var input = this.querySelector('.input--small');
        return input ? input.value : '';
      } catch (error) {
        return '';
      }
    };

    /**
     * Validity test. Trimmed, so a value of only whitespace does not count as
     * filled. The value actually SENT to the cart is left untrimmed (see
     * pmiscSyncHiddenInput) because the pills/select option labels come from an
     * untrimmed `split: ','` (snippets/custom-product-field.liquid:4-8, :80, :119)
     * and must match what Liquid rendered.
     */
    proto.pmiscIsFilled = function () {
      return String(this.pmiscGetValue()).trim() !== '';
    };

    /* ---------------------------------------------------------------- *
     * Line-item property delivery                                       *
     * ---------------------------------------------------------------- */

    /** The section's main add-to-cart form — sections/main-product.liquid:119. */
    proto.pmiscGetForm = function () {
      if (!this.pmiscSectionId) return null;
      return document.getElementById('product-form-' + this.pmiscSectionId);
    };

    /**
     * Keeps a hidden `properties[<data-name>]` input inside the section's form
     * in sync with the current value.
     *
     * A hidden input inside the <form> is the only mechanism that serves BOTH
     * add-to-cart paths:
     *   - the AJAX path, whichever cluster owns it, as long as it builds its
     *     body from `new FormData(form)` (the Dawn convention this theme's
     *     <product-form> follows), and
     *   - the native dynamic-checkout POST performed by
     *     `{{ form | payment_button }}` (snippets/buy-buttons.liquid:189-191),
     *     which no JS intercepts.
     *
     * It deliberately does NOT write into
     * `div.hidden.product-form__variants[data-values=""]`
     * (snippets/buy-buttons.liquid:87) — that node's name and empty
     * `data-values` mark it as a <product-form>-owned bundle/variant payload,
     * not a line-item-property slot.
     *
     * An empty value removes the input entirely rather than sending an empty
     * property, so a blank optional field leaves no trace on the order.
     */
    proto.pmiscSyncHiddenInput = function () {
      var form = this.pmiscGetForm();
      if (!form || !this.pmiscFieldName) return;

      var value = this.pmiscGetValue();
      var ownerKey = this.id || ('cpf-' + this.pmiscSectionId + '-' + this.pmiscFieldName);
      var selector = 'input[type="hidden"][data-pmisc-owner="' + pmiscAttrEscape(ownerKey) + '"]';

      var input = form.querySelector(selector);

      if (String(value).trim() === '') {
        if (input && input.parentNode) input.parentNode.removeChild(input);
        this.pmiscHiddenInput = null;
        return;
      }

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.setAttribute('data-pmisc-owner', ownerKey);
        form.appendChild(input);
      }

      // Schema info at sections/main-product.liquid:10699: "Custom property name
      // that's sent to order information."
      input.name = 'properties[' + this.pmiscFieldName + ']';
      input.value = value;
      this.pmiscHiddenInput = input;
    };

    proto.pmiscRemoveHiddenInput = function () {
      var ownerKey = this.id || ('cpf-' + this.pmiscSectionId + '-' + this.pmiscFieldName);
      var form = this.pmiscGetForm();
      var input =
        (form && form.querySelector('input[type="hidden"][data-pmisc-owner="' + pmiscAttrEscape(ownerKey) + '"]')) ||
        this.pmiscHiddenInput;

      if (input && input.parentNode) input.parentNode.removeChild(input);
      this.pmiscHiddenInput = null;
    };

    /* ---------------------------------------------------------------- *
     * Field-level error                                                 *
     * ---------------------------------------------------------------- */

    /**
     * Toggles `input--error` on the control.
     *
     * assets/base.css drives the message with an ADJACENT-SIBLING rule:
     *   `.input--error, .input--error:focus {border-color:red;border-width:2px}`
     *   `.input--error + .input-error-msg {display:block}`
     * and `span.input-error-msg` is the LAST child of `.product-form__input`
     * (snippets/custom-product-field.liquid:145). So the class has to go on the
     * error span's IMMEDIATE PREVIOUS SIBLING.
     *
     * That sibling is the control itself only for text / number / textarea
     * (:37-48, :50-64, :66-73). For select it is `div.select` (:133), for
     * swatches `div.color-swatches-container` (:94) and for pills the LAST
     * <label> (:85-87) — none of which could be styled meaningfully. Those three
     * types can never be empty anyway (pills/swatches check index 0 at :81-83 /
     * :120-122; select falls back to its first option), so the path is only ever
     * exercised by the free-text types, and this method bails out for the rest.
     */
    proto.pmiscUpdateFieldError = function () {
      if (!pmiscIsFreeTextType(this.pmiscType)) return;

      var errorSpan = this.querySelector('.input-error-msg');
      if (!errorSpan) return;

      var control = errorSpan.previousElementSibling;
      if (!control || !control.classList || !control.classList.contains('input--small')) return;

      var showError =
        this.dataset.required === 'true' && this.pmiscTouched && !this.pmiscIsFilled();

      // classList.toggle's second argument is well supported; kept explicit for clarity.
      if (showError) control.classList.add('input--error');
      else control.classList.remove('input--error');
    };

    /* ---------------------------------------------------------------- *
     * Wiring                                                            *
     * ---------------------------------------------------------------- */

    proto.pmiscHandleChange = function () {
      this.pmiscTouched = true;
      this.pmiscSyncHiddenInput();
      this.pmiscUpdateFieldError();
      PmiscFieldRegistry.refresh(this.pmiscSectionId);
    };

    proto.pmiscBindControls = function () {
      this.pmiscUnbindControls();

      var handler = pmiscGuard(this.pmiscHandleChange, this);
      this.pmiscBound = [];

      var self = this;
      function bind(element, type) {
        element.addEventListener(type, handler);
        self.pmiscBound.push({ element: element, type: type, handler: handler });
      }

      if (this.pmiscType === 'pills') {
        // Covers both the pills markup (:76-84) and the swatches markup
        // (:115-123), since :16 aliases swatches to pills.
        var radios = pmiscQueryAll('input[type="radio"]', this);
        for (var r = 0; r < radios.length; r++) bind(radios[r], 'change');
        return;
      }

      if (this.pmiscType === 'select') {
        var select = this.querySelector('select.select__select'); // :134-141
        if (select) bind(select, 'change');
        return;
      }

      // text / number / textarea — :39, :52, :67.
      // `change` as well as `input` so autofill and the number spinner are caught.
      var input = this.querySelector('.input--small');
      if (input) {
        bind(input, 'input');
        bind(input, 'change');
      }
    };

    proto.pmiscUnbindControls = function () {
      if (!this.pmiscBound) return;
      for (var i = 0; i < this.pmiscBound.length; i++) {
        var entry = this.pmiscBound[i];
        try {
          entry.element.removeEventListener(entry.type, entry.handler);
        } catch (error) {
          /* element already gone — nothing to detach */
        }
      }
      this.pmiscBound = null;
    };

    return PmiscCustomProductField;
  })();

  pmiscDefine('custom-product-field', PmiscCustomProductField);

  /* ------------------------------------------------------------------ *
   * Late sweeps.                                                        *
   *                                                                     *
   * A field can upgrade before the buy_buttons block has been parsed     *
   * (the block loop at sections/main-product.liquid:121-122 renders in    *
   * merchant order), so the counters and the gate are re-applied once the *
   * document is complete, and again after a theme-editor section load.    *
   * ------------------------------------------------------------------ */
  pmiscWhenReady(pmiscGuard(function () {
    PmiscFieldRegistry.refreshAll();
  }));

  document.addEventListener('shopify:section:load', pmiscGuard(function () {
    PmiscFieldRegistry.refreshAll();
  }));

  /* ------------------------------------------------------------------ *
   * Public, read-only API for the <product-form> cluster.                *
   *                                                                     *
   * Nothing in the theme requires this — no file listens for an event    *
   * from <custom-product-field> — but the add-to-cart gate and the       *
   * line-item properties are a joint concern with <product-form>, and    *
   * this lets that cluster consult this one instead of reimplementing    *
   * the rules. Namespaced so it cannot collide with window.routes /      *
   * window.cartStrings / window.variantStrings (layout/theme.liquid:670-699). *
   * ------------------------------------------------------------------ */
  window.themeCustomProductFields = {
    /** True when a required field in this section is still empty. */
    isSectionGated: function (sectionId) {
      return PmiscFieldRegistry.isGated(sectionId);
    },

    /** Forces a recompute for one section (or all sections when omitted). */
    refresh: function (sectionId) {
      if (sectionId) PmiscFieldRegistry.refresh(sectionId);
      else PmiscFieldRegistry.refreshAll();
    },

    /**
     * { "<field_name>": "<value>" } for one section — the same values already
     * mirrored into `properties[...]` hidden inputs on the section's form, for
     * a caller that would rather build its body by hand than from FormData.
     */
    getProperties: function (sectionId) {
      var properties = {};
      var fields = PmiscFieldRegistry.fieldsForSection(sectionId);
      for (var i = 0; i < fields.length; i++) {
        var field = fields[i];
        var name = field.dataset.name || '';
        if (!name || typeof field.pmiscGetValue !== 'function') continue;
        var value = field.pmiscGetValue();
        if (String(value).trim() === '') continue;
        properties[name] = value;
      }
      return properties;
    },

    /** Resolves the section an add-to-cart control belongs to. */
    resolveSectionId: function (element) {
      return PmiscFieldRegistry.resolveSectionId(element);
    }
  };
})();
